import { useEffect, useState } from 'react';
import './FlexibleEmployment.scss';

function emptyJob() {
  return {
    jobTitle: '',
    jobDescription: '',
    fromDate: '',
    toDate: '',
    adClosingDate: '',
    jobLocation: '',
    cityName: '',
    numberOfPeopleRequired: 1,
    hourlyRate: '',
    employmentType: 'casual',
    jobStatus: 'open',
    skillsRequired: ['', '', ''],
  };
}

function normalizeSkills(skills) {
  const normalized = Array.isArray(skills)
    ? skills
    : typeof skills === 'string'
      ? skills.split(',')
      : [];

  return [0, 1, 2].map((index) => normalized[index] || '');
}

export default function EmploymentJobForm({
  initialJob,
  onSubmit,
  submitLabel,
  submitting,
  onCancel,
}) {
  const [formData, setFormData] = useState(emptyJob());
  const [error, setError] = useState('');

  useEffect(() => {
    setFormData({
      ...emptyJob(),
      ...(initialJob || {}),
      skillsRequired: normalizeSkills(initialJob?.skillsRequired),
    });
    setError('');
  }, [initialJob]);

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const updateSkill = (index, value) => {
    setFormData((prev) => {
      const nextSkills = [...prev.skillsRequired];
      nextSkills[index] = value;
      return {
        ...prev,
        skillsRequired: nextSkills,
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.jobTitle.trim() || !formData.jobDescription.trim()) {
      setError('Role title and job description are required.');
      return;
    }

    if (!formData.adClosingDate) {
      setError('ad closing date are required.');
      return;
    }

    setError('');

    await onSubmit({
      ...formData,
      jobTitle: formData.jobTitle.trim(),
      jobDescription: formData.jobDescription.trim(),
      jobLocation: formData.jobLocation.trim(),
      cityName: (formData.cityName || formData.jobLocation || '').trim(),
      hourlyRate: formData.hourlyRate === '' ? '' : Number(formData.hourlyRate),
      numberOfPeopleRequired: Number(formData.numberOfPeopleRequired || 0),
      skillsRequired: formData.skillsRequired
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 3),
    });
  };

  return (
    <form className="flex-employment-form" onSubmit={handleSubmit}>
      <div>
        <h2 className="flex-employment-section-title">Job details</h2>
        <p>Advertise temporary staffing needs without mixing them into trade-job workflows.</p>
      </div>

      {error && <div className="flex-employment-error">{error}</div>}

      <div className="flex-employment-form-grid">
        <div className="flex-employment-field full-width">
          <label htmlFor="employment-job-title">Temporary role title</label>
          <input
            id="employment-job-title"
            type="text"
            value={formData.jobTitle}
            onChange={(event) => updateField('jobTitle', event.target.value)}
            placeholder="Barista needed for X Cafe"
          />
        </div>

        <div className="flex-employment-field full-width">
          <label htmlFor="employment-job-description">Job description</label>
          <textarea
            id="employment-job-description"
            value={formData.jobDescription}
            onChange={(event) => updateField('jobDescription', event.target.value)}
            placeholder="Describe the temporary work, shift expectations, and any special requirements"
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-from-date">Required from date</label>
          {(
  <input
    id="employment-job-from-date"
    type="date"
    value={formData.fromDate}
    onChange={(event) => updateField('fromDate', event.target.value)}
  />
)}
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-to-date">Required to date</label>
          { (
  <input
    id="employment-job-to-date"
    type="date"
    value={formData.toDate}
    onChange={(event) => updateField('toDate', event.target.value)}
  />
)}
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-closing-date">Ad closing date</label>
          <input
            id="employment-job-closing-date"
            type="date"
            value={formData.adClosingDate}
            onChange={(event) => updateField('adClosingDate', event.target.value)}
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-type">Employment type</label>
          <select
            id="employment-job-type"
            value={formData.employmentType}
            onChange={(event) => updateField('employmentType', event.target.value)}
          >
            <option value="casual">Casual</option>
            <option value="Parttime">Part-time</option>
            <option value="Fulltime">Full-time</option>
            <option value="contract">Contract</option>
          </select>
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-location">Location</label>
          <input
            id="employment-job-location"
            type="text"
            value={formData.jobLocation}
            onChange={(event) => updateField('jobLocation', event.target.value)}
            placeholder="Canberra / Fyshwick"
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-city">City name</label>
          <input
            id="employment-job-city"
            type="text"
            value={formData.cityName}
            onChange={(event) => updateField('cityName', event.target.value)}
            placeholder="Canberra"
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-headcount">Number of people required</label>
          <input
            id="employment-job-headcount"
            type="number"
            min="1"
            value={formData.numberOfPeopleRequired}
            onChange={(event) => updateField('numberOfPeopleRequired', event.target.value)}
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-rate">Hourly rate</label>
          <input
            id="employment-job-rate"
            type="number"
            min="0"
            step="0.01"
            value={formData.hourlyRate}
            onChange={(event) => updateField('hourlyRate', event.target.value)}
            placeholder="35.00"
          />
        </div>

        <div className="flex-employment-field">
          <label htmlFor="employment-job-status">Job status</label>
          <select
            id="employment-job-status"
            value={formData.jobStatus}
            onChange={(event) => updateField('jobStatus', event.target.value)}
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        {[0, 1, 2].map((index) => (
          <div className="flex-employment-field" key={`skill-${index}`}>
            <label htmlFor={`employment-job-skill-${index}`}>Skill {index + 1}</label>
            <input
              id={`employment-job-skill-${index}`}
              type="text"
              value={formData.skillsRequired[index] || ''}
              onChange={(event) => updateSkill(index, event.target.value)}
              placeholder="Optional"
            />
          </div>
        ))}
      </div>

      <div className="flex-employment-card-actions">
        <button className="flex-employment-button" type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : submitLabel}
        </button>
        {onCancel && (
          <button className="flex-employment-button-secondary" type="button" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}